/**
 * Feed Composer update — pure state transition logic.
 *
 * Elm-ish architecture: this function is pure.
 * - No API calls
 * - No storage access
 * - No hooks
 * - No side effects
 * - No mutation
 * - No Date.now(), Math.random(), UUID, storage, or browser APIs
 *
 * Each message produces a new model and any effects that need to run.
 *
 * CORRECTION02 changes:
 * - nextSubmissionId counter replaces Date.now() for deterministic IDs (R2)
 * - Started is idempotent: emits LoadAuthor only when status === 'idle' (R3)
 * - SubmitPost effect carries submissionId for correlation (R5)
 * - PublishClicked guards: form state + no in-flight submission (R5)
 * - PublishSucceeded/PublishFailed check submissionId match (R5)
 * - Default branch replaced with assertNever() for exhaustiveness (R6)
 */
import type { FeedComposerModel } from './model';
import { countCharacters, validateContent } from './model';
import type { ComposerMsg } from './messages';

// =============================================================================
// Exhaustiveness helper
// =============================================================================

/**
 * Compile-time exhaustiveness check.
 * If this is reached, a message variant is not handled.
 * Does NOT include user content.
 */
function assertNever(value: never): never {
  throw new Error(`Unhandled feed composer message: ${String(value)}`);
}

// =============================================================================
// Effect types
// =============================================================================

/**
 * Effect types that can be triggered by state transitions.
 * These are handled by the effect runner (useFeedComposerProgram.ts).
 *
 * CORRECTION01: SubmitPost carries submissionId for correlation (R5).
 */
export type ComposerEffect =
  /** Load the authenticated user's profile for the author block */
  | { readonly type: 'LoadAuthor' }
  /** Submit the post to the backend */
  | {
      readonly type: 'SubmitPost';
      readonly submissionId: number;
      readonly content: string;
      readonly anonymous: boolean;
    };

// =============================================================================
// Update result
// =============================================================================

/**
 * Result of an update call — new model plus effects to run.
 */
export interface UpdateResult {
  readonly model: FeedComposerModel;
  readonly effects: readonly ComposerEffect[];
}

// =============================================================================
// Pure update function
// =============================================================================

/**
 * Pure update function — the heart of the Elm-ish architecture.
 *
 * @param model — Current state
 * @param msg — Message triggering the transition
 * @returns New state and effects to run
 */
export function update(
  model: FeedComposerModel,
  msg: ComposerMsg
): UpdateResult {
  switch (msg.type) {
    case 'Started':
      // Idempotent: only start when truly idle (R3)
      if (model.status !== 'idle') {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [{ type: 'LoadAuthor' }],
      };

    case 'AuthorLoadFailed':
      return {
        model: {
          ...model,
          status: 'form',
          author: null,
        },
        effects: [],
      };

    case 'AuthorLoaded':
      return {
        model: {
          ...model,
          status: 'form',
          author: msg.author,
        },
        effects: [],
      };

    case 'ContentChanged': {
      const charCount = countCharacters(msg.content);
      const validationErrors = validateContent(msg.content);
      return {
        model: {
          ...model,
          content: msg.content,
          charCount,
          validationErrors,
        },
        effects: [],
      };
    }

    case 'AnonymousToggled':
      return {
        model: {
          ...model,
          anonymous: msg.anonymous,
        },
        effects: [],
      };

    case 'PublishClicked': {
      // Guard: no action if not in form state
      if (model.status !== 'form') {
        return { model, effects: [] };
      }
      // Guard: no action if a submission is already in-flight (R5)
      if (model.activeSubmissionId !== null) {
        return { model, effects: [] };
      }
      const errors = validateContent(model.content);
      if (errors.length > 0) {
        return {
          model: {
            ...model,
            validationErrors: errors,
          },
          effects: [],
        };
      }
      // Generate deterministic submission identity from pure counter (R2)
      const submissionId = model.nextSubmissionId;
      return {
        model: {
          ...model,
          status: 'submitting',
          validationErrors: [],
          error: null,
          activeSubmissionId: submissionId,
          nextSubmissionId: submissionId + 1,
        },
        effects: [
          {
            type: 'SubmitPost',
            submissionId,
            content: model.content,
            anonymous: model.anonymous,
          },
        ],
      };
    }

    case 'PublishSucceeded': {
      // Guard: ignore stale completions (R5)
      if (model.activeSubmissionId !== msg.submissionId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'success',
          postId: msg.postId,
          error: null,
          activeSubmissionId: null,
        },
        effects: [],
      };
    }

    case 'PublishFailed': {
      // Guard: ignore stale completions (R5)
      if (model.activeSubmissionId !== msg.submissionId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'error',
          error: msg.error,
          activeSubmissionId: null,
        },
        effects: [],
      };
    }

    case 'RetryClicked':
      return {
        model: {
          ...model,
          status: 'form',
          error: null,
        },
        effects: [],
      };

    case 'CancelClicked':
      // Cancel preserves the draft in memory but signals navigation intent.
      // Navigation is handled outside the reducer (in the page component).
      return {
        model: {
          ...model,
          error: null,
        },
        effects: [],
      };

    default:
      // Compile-time exhaustiveness check (R6)
      return assertNever(msg);
  }
}
